import { forwardRef, useState, useEffect, useCallback } from 'react';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { cn } from '../../utils/cn';
import { useDebounce } from '../../hooks/useDebounce';
import { CALLER_FONT_SIZE } from './fieldFontSize';

interface SearchInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'value' | 'type'
  > {
  /** Current search value (controlled) */
  value?: string;
  /** Called with the debounced search value (300ms delay) */
  onSearch?: (value: string) => void;
  /** Called immediately on every keystroke */
  onChange?: (value: string) => void;
  /** Debounce delay in milliseconds (default 300) */
  debounceMs?: number;
  /** Error message -- turns border red */
  error?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Search input with leading MagnifyingGlass icon, clear button, and
 * debounced `onSearch` callback for search-as-you-type patterns.
 *
 * - `onChange` fires immediately on every keystroke
 * - `onSearch` fires after `debounceMs` of inactivity
 * - Clear button resets instantly (no debounce on clear)
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput(
    {
      value: controlledValue,
      onSearch,
      onChange,
      debounceMs = 300,
      error,
      className,
      placeholder = 'Buscar...',
      id,
      ...props
    },
    ref,
  ) {
    const isControlled = controlledValue !== undefined;
    const [internalValue, setInternalValue] = useState('');
    const currentValue = isControlled ? controlledValue : internalValue;

    const debouncedValue = useDebounce(currentValue, debounceMs);

    // Fire debounced search callback
    useEffect(() => {
      onSearch?.(debouncedValue);
    }, [debouncedValue, onSearch]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;
        if (!isControlled) {
          setInternalValue(next);
        }
        onChange?.(next);
      },
      [isControlled, onChange],
    );

    const handleClear = useCallback(() => {
      if (!isControlled) {
        setInternalValue('');
      }
      onChange?.('');
      // Fire search immediately on clear (bypass debounce)
      onSearch?.('');
    }, [isControlled, onChange, onSearch]);

    const inputId = id || 'search-input';
    const hasValue = currentValue.length > 0;

    return (
      <div className="relative">
        {/* Leading magnifying glass icon */}
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none">
          <MagnifyingGlass size={18} />
        </span>

        <input
          ref={ref}
          id={inputId}
          type="search"
          value={currentValue}
          onChange={handleChange}
          placeholder={placeholder}
          className={cn(
            'w-full pl-10 pr-10 py-2 rounded-lg text-text-primary bg-bg-secondary min-h-[var(--touch-target-min)] lg:min-h-0',
            // >= 16px below lg (no iOS zoom on focus), 14px from lg; a caller font size replaces both
            !CALLER_FONT_SIZE.test(className ?? '') && 'text-base lg:text-sm',
            'border border-border-control outline-none transition-all duration-200',
            'placeholder:text-text-tertiary',
            'focus:border-border-focus focus:ring-1 focus:ring-accent-500/30',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            error &&
              'border-border-error focus:border-border-error focus:ring-error/30',
            className,
          )}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
          aria-label={props['aria-label'] || 'Buscar'}
          {...props}
        />

        {/* Clear button -- visible only when there is a value */}
        {hasValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary transition-colors cursor-pointer"
            aria-label="Limpar busca"
            tabIndex={-1}
          >
            <X size={16} />
          </button>
        )}

        {error && (
          <p
            id={`${inputId}-error`}
            className="mt-1.5 text-xs text-error-text"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  },
);

export default SearchInput;
